import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Lang = 'en' | 'ar';

interface LanguageContextType {
  currentLanguage: Lang;
  isRtl: boolean;
  changeLanguage: (lang: Lang) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: 'en',
  isRtl: false,
  changeLanguage: () => {},
  toggleLanguage: () => {},
});

export const useLanguage = () => useContext(LanguageContext);

const normalize = (lng?: string): Lang => (lng?.startsWith('ar') ? 'ar' : 'en');

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<Lang>(normalize(i18n.language));

  useEffect(() => {
    const handler = (lng: string) => setCurrentLanguage(normalize(lng));
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, [i18n]);

  const changeLanguage = useCallback((lang: Lang) => { i18n.changeLanguage(lang); }, [i18n]);
  const toggleLanguage = useCallback(
    () => changeLanguage(currentLanguage === 'ar' ? 'en' : 'ar'),
    [changeLanguage, currentLanguage]
  );

  return (
    <LanguageContext.Provider
      value={{ currentLanguage, isRtl: currentLanguage === 'ar', changeLanguage, toggleLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
